import { SONG_SERVICES, type SongService, songSearchUrl } from '../../domain/song-search';
import { type Lang, translate } from '../../i18n';
import { el, icon } from '../../lib/dom';
import { fillSheet, presentSheet, syncSheetTrigger } from './action-sheet';

/**
 * »Poišči skladbo« (P2 §3.12, §4.8) — kebab meni s tremi storitvami.
 *
 * **Brez API-ja, brez računa, brez odvisnosti.** Vsaka postavka je navadna
 * povezava na javno iskalno stran storitve (`domain/song-search.ts`). Radio je
 * odkrivanje: skladbo slišiš enkrat in nimaš kam po njo — to je edina funkcija
 * na seznamu, pri kateri vsi konkurenti odpovejo, ker jo vidijo kot integracijo.
 *
 * ⚠️ **Postavke so `<a>`, ne `<button>`.** Povezava pove, kam pelje, prenese
 * srednji klik in dolgi pritisk ter jo je mogoče odpreti v ozadju. Gumb, ki
 * kliče `window.open()`, tega ne zna in ga blokirniki pojavnih oken ustavijo.
 *
 * `target="_blank"` z `rel="noopener noreferrer"`: nova stran ne sme dobiti
 * `window.opener` — in predvsem se **aplikacija ne sme zapustiti**, ker bi to
 * v nameščeni PWA pomenilo prekinjen zvok.
 */

export interface SongMenuNode {
  root: HTMLElement;
  trigger: HTMLButtonElement;
  /**
   * Poveži meni z eno skladbo.
   *
   * `query === null` meni **skrije**: postaja, ki naslova ne pošlje, je pogosto
   * stanje in ne napaka (P2 §3.1), gumb brez cilja pa je vabilo v prazno.
   */
  update(lang: Lang, query: string | null): void;
}

/** Ime storitve je znamka in se ne prevaja. */
const SERVICE_NAMES: Record<SongService, string> = {
  apple: 'Apple Music',
  spotify: 'Spotify',
  youtube: 'YouTube',
};

export function createSongMenu(triggerClass: string, iconSize = 20): SongMenuNode {
  const trigger = el('button', {
    class: triggerClass,
    type: 'button',
    'data-menu': '',
    'aria-haspopup': 'menu',
    'aria-expanded': 'false',
  });
  trigger.append(icon('i-search', iconSize));

  const title = el('div', { class: 'dd__title' });

  const items = SONG_SERVICES.map((service) => {
    const label = el('span');
    const link = el(
      'a',
      {
        class: 'dd__item',
        role: 'menuitem',
        target: '_blank',
        rel: 'noopener noreferrer',
        href: '#',
      },
      [icon('i-external', 16), label],
    );
    return { service, link, label };
  });

  const root = el('span', { class: 'menu-anchor nw-song-menu' }, [
    trigger,
    el('div', { class: 'dd', role: 'menu' }, [title, ...items.map((item) => item.link)]),
  ]);

  /**
   * Na dotiku iste tri storitve ponudi bottom sheet.
   *
   * Meni stoji **v medijski vrstici in v razširjenem pogledu**, torej ob
   * spodnjem robu — mesto, kjer je `.dd` najtesnejši in kjer ga je uporabnik
   * na napravi že enkrat izgubil pod robom. Postavke ostanejo povezave, zato
   * srednji klik in dolgi pritisk delujeta tudi tu.
   */
  syncSheetTrigger(trigger);
  trigger.addEventListener('click', () => {
    if (!syncSheetTrigger(trigger)) return;
    fillSheet(
      title.textContent ?? '',
      items
        .filter((item) => !item.link.hasAttribute('hidden'))
        .map((item) => ({
          icon: 'i-external',
          label: item.label.textContent ?? '',
          ariaLabel: item.link.getAttribute('aria-label') ?? undefined,
          href: item.link.href,
        })),
    );
    presentSheet(trigger);
  });

  return {
    root,
    trigger,
    update(lang, query) {
      const hidden = query === null;
      root.toggleAttribute('hidden', hidden);
      if (hidden) return;

      const label = translate(lang, 'song.find');
      trigger.setAttribute('aria-label', label);
      trigger.title = label;
      title.textContent = label;

      for (const item of items) {
        const url = songSearchUrl(item.service, query);
        // Prazno iskanje nima naslova; postavka takrat odpade, meni pa ostane —
        // druge storitve so še vedno dosegljive.
        item.link.toggleAttribute('hidden', url === null);
        if (url !== null) item.link.href = url;
        item.label.textContent = SERVICE_NAMES[item.service];
        item.link.setAttribute(
          'aria-label',
          translate(lang, 'song.findOn', { service: SERVICE_NAMES[item.service] }),
        );
      }
    },
  };
}
